import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FiBell, FiSettings, FiX } from "react-icons/fi";
import { FaChevronDown } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { SIDEBAR_LOGO_WORDMARK } from "../assets";
import { ownerSelector } from "../../store/store";
import {
  refreshInboxUnreadChatsCount,
  subscribeInboxUnreadChatsCount,
} from "../../utils/inboxUnread";
import { isAiPosterAllowedForEmail } from "../../utils/aiContent";
import WorkSpaceSelectionModal from "../Modals/WorkSpaceSelectionModal";

type NavItemBase = {
  key: string;
  label: string;
  path: string;
  matcher: (pathname: string) => boolean;
  badge?: number;
};

type NavItemImage = NavItemBase & {
  iconDefaultSrc: string;
  iconActiveSrc?: string;
};

type NavItemLibrary = NavItemBase & {
  renderIcon: () => ReactNode;
};

type NavItem = NavItemImage | NavItemLibrary;

type SidebarProps = {
  mobileOpen?: boolean;
  onClose?: () => void;
};

const IconOverview = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1z" />
  </svg>
);

const IconLeads = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5.5 19.5c.8-3.2 3.3-5 6.5-5s5.7 1.8 6.5 5" />
  </svg>
);

const IconLists = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden
  >
    <rect x="3.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="3.5" width="7" height="7" rx="1" />
    <rect x="3.5" y="13.5" width="7" height="7" rx="1" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1" />
  </svg>
);

const IconCampaigns = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="7.5" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const IconConversations = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M5 16.5 4 20l3.8-1.4A8.5 8.5 0 1 0 5 16.5Z" />
  </svg>
);

const IconTemplates = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    aria-hidden
  >
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <path d="M8 9h8M8 12.5h8M8 16h5" />
  </svg>
);

const IconAiContent = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
    <path d="M6.5 6.5 8.5 8.5M15.5 15.5l2 2M17.5 6.5 15.5 8.5M8.5 15.5l-2 2" />
    <circle cx="12" cy="12" r="3.5" />
  </svg>
);

const IconIntegrations = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3.5" y="3.5" width="7" height="7" rx="1.5" />
    <rect x="13.5" y="13.5" width="7" height="7" rx="1.5" />
    <path d="M10.5 7.5h3A2 2 0 0 1 15.5 9.5v3" />
  </svg>
);

const IconAccounts = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="9" cy="8" r="3" />
    <path d="M4 19c.7-3 2.8-4.5 5-4.5s4.3 1.5 5 4.5" />
    <circle cx="16.5" cy="8.5" r="2.2" />
    <path d="M16.5 14.5c2 .2 3.6 1.4 4.2 3.5" />
  </svg>
);

const IconPipeline = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="3" y="4" width="4" height="16" rx="1" />
    <rect x="10" y="4" width="4" height="10" rx="1" />
    <rect x="17" y="4" width="4" height="13" rx="1" />
  </svg>
);

const homeNav: NavItem[] = [
  {
    key: "overview",
    label: "Overview",
    path: "/",
    matcher: (pathname) => pathname === "/",
    renderIcon: () => <IconOverview />,
  },
  {
    key: "pipeline",
    label: "Pipeline Overview",
    path: "/pipeline",
    matcher: (pathname) => pathname.startsWith("/pipeline"),
    renderIcon: () => <IconPipeline />,
  },
  {
    key: "ai-poster",
    label: "AI Poster",
    path: "/ai-content/plan",
    matcher: (pathname) => pathname.startsWith("/ai-content"),
    renderIcon: () => <IconAiContent />,
  },
];

const crmNav: NavItem[] = [
  {
    key: "leads",
    label: "Leads Database",
    path: "/leads",
    matcher: (pathname) => pathname.startsWith("/leads"),
    renderIcon: () => <IconLeads />,
  },
  {
    key: "lists",
    label: "Lists",
    path: "/prospects",
    matcher: (pathname) => pathname.startsWith("/prospects"),
    renderIcon: () => <IconLists />,
  },
];

const engageNav: NavItem[] = [
  {
    key: "campaigns",
    label: "Campaigns",
    path: "/campaign-management",
    matcher: (pathname) =>
      pathname.startsWith("/campaign-management") || pathname.includes("/campaigns"),
    renderIcon: () => <IconCampaigns />,
  },
  {
    key: "inbox",
    label: "Conversations",
    path: "/inbox",
    matcher: (pathname) => pathname.startsWith("/inbox"),
    renderIcon: () => <IconConversations />,
  },
  {
    key: "templates",
    label: "Templates",
    path: "/templates",
    matcher: (pathname) => pathname.startsWith("/templates") || pathname.startsWith("/campaigns/template"),
    renderIcon: () => <IconTemplates />,
  },
];

const workspaceNav: NavItem[] = [
  {
    key: "integrations",
    label: "Integrations",
    path: "/integrations",
    matcher: (pathname) => pathname.startsWith("/integrations"),
    renderIcon: () => <IconIntegrations />,
  },
  {
    key: "settings",
    label: "Settings",
    path: "/settings/profile",
    matcher: (pathname) => pathname.startsWith("/settings"),
    renderIcon: () => <FiSettings className="h-[17px] w-[17px] shrink-0" aria-hidden />,
  },
];

const IconDailyReports = () => (
  <svg
    viewBox="0 0 24 24"
    className="h-[17px] w-[17px] shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);

const analyticsNav: NavItem[] = [
  {
    key: "daily-reports",
    label: "Daily Reports",
    path: "/daily-reports",
    matcher: (pathname) => pathname.startsWith("/daily-reports"),
    renderIcon: () => <IconDailyReports />,
  },
  {
    key: "email-campaign-analytics",
    label: "Email campaigns",
    path: "/analytics/email-campaigns",
    matcher: (pathname) => pathname.startsWith("/analytics/email-campaigns"),
    renderIcon: () => (
      <svg
        viewBox="0 0 24 24"
        className="h-[17px] w-[17px] shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <path d="m3 7 9 6 9-6" />
      </svg>
    ),
  },
  {
    key: "error-logs",
    label: "Error Log",
    path: "/error-logs",
    matcher: (pathname) => pathname.startsWith("/error-logs"),
    renderIcon: () => (
      <svg
        viewBox="0 0 24 24"
        className="h-[17px] w-[17px] shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
      </svg>
    ),
  },
];

const accountsNav: NavItem[] = [
  {
    key: "accounts",
    label: "Sending Accounts",
    path: "/teams",
    matcher: (pathname) => pathname.includes("/teams"),
    renderIcon: () => <IconAccounts />,
  },
];

const bottomNav: NavItem[] = [
  {
    key: "notifications",
    label: "Notifications",
    path: "/notifications",
    matcher: (pathname) => pathname.startsWith("/notifications"),
    renderIcon: () => <FiBell className="h-[17px] w-[17px] shrink-0" aria-hidden />,
  },
];

const getWorkspaceInitials = (name?: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "WS";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const Sidebar = ({ mobileOpen = false, onClose }: SidebarProps) => {
  const { pathname } = useLocation();
  const { owner, currentUser, currentWorkspace } = useSelector(ownerSelector);
  const [inboxUnreadCount, setInboxUnreadCount] = useState(0);
  const [showWorkSpaceOptions, setShowWorkSpaceOptions] = useState(false);
  const isOnInbox = pathname.startsWith("/inbox");
  const isOnInboxRef = useRef(isOnInbox);
  const hasLinkedOwner = Boolean(owner?._id);
  const workspaceName = currentWorkspace?.name || "Workspace";
  const visibleHomeNav = homeNav.filter((item) => {
    if (item.key !== "ai-poster") return true;
    return isAiPosterAllowedForEmail(currentUser?.email);
  });

  useEffect(() => {
    isOnInboxRef.current = isOnInbox;
  }, [isOnInbox]);

  const refreshInboxUnreadCount = useCallback(async () => {
    if (isOnInboxRef.current) return;
    await refreshInboxUnreadChatsCount({
      userId: currentUser?.userId,
      workspaceId: currentWorkspace?._id,
      ownerId: owner?._id,
    });
  }, [currentUser?.userId, currentWorkspace?._id, owner?._id]);

  useEffect(() => subscribeInboxUnreadChatsCount(setInboxUnreadCount), []);

  useEffect(() => {
    if (isOnInbox) return;
    void refreshInboxUnreadCount();
    const interval = window.setInterval(() => {
      void refreshInboxUnreadCount();
    }, 45000);
    return () => window.clearInterval(interval);
  }, [isOnInbox, refreshInboxUnreadCount]);

  const renderNavItem = (item: NavItem) => {
    const isActive = item.matcher(pathname);
    const badgeCount = item.key === "inbox" ? inboxUnreadCount : item.badge || 0;
    const icon =
      "renderIcon" in item ? (
        item.renderIcon()
      ) : (
        <img
          src={item.iconDefaultSrc}
          alt=""
          className="h-[17px] w-[17px] shrink-0"
          aria-hidden="true"
        />
      );

    return (
      <li key={item.key}>
        <Link
          to={item.path}
          state={item.key === "inbox" ? { inboxReset: true } : undefined}
          onClick={onClose}
          className={`flex items-center justify-between rounded-[7px] px-2.5 py-[9px] text-sm transition-colors ${
            isActive
              ? "bg-sidebar-hover font-semibold text-white shadow-[inset_3px_0_0_0_var(--color-bright-green)]"
              : "font-medium text-sidebar-text hover:bg-sidebar-hover hover:text-white"
          }`}
        >
          <span className="flex items-center gap-[11px] [&_svg]:text-current">
            {icon}
            <span className="leading-none">{item.label}</span>
          </span>
          {badgeCount > 0 ? (
            <span
              className={`inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold ${
                item.key === "inbox"
                  ? "bg-bright-green text-dark-blue"
                  : "bg-danger text-white"
              }`}
            >
              {badgeCount > 99 ? "99+" : badgeCount}
            </span>
          ) : null}
        </Link>
      </li>
    );
  };

  const navBody = (
    <div className="flex h-full flex-col justify-between">
      <div>
        <div className="mb-6 flex items-center justify-between gap-2 px-1.5">
          <div className="flex min-w-0 items-center">
            <img
              src={SIDEBAR_LOGO_WORDMARK}
              alt="Innlead"
              className="h-8 w-auto max-w-[160px] object-contain object-left"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-white lg:hidden"
            aria-label="Close menu"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <button
          type="button"
          onClick={() => setShowWorkSpaceOptions(true)}
          disabled={!hasLinkedOwner}
          className="mb-6 flex w-full items-center justify-between rounded-[9px] border border-white/10 bg-sidebar-chip px-2.5 py-2.5 text-left text-xs text-sidebar-fg disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="flex min-w-0 items-center">
            <span className="mr-2 grid h-[25px] w-[25px] shrink-0 place-items-center rounded-full bg-sidebar-avatar text-[10px] font-semibold text-white">
              {getWorkspaceInitials(workspaceName)}
            </span>
            <span className="truncate">{workspaceName}</span>
          </span>
          <FaChevronDown className="ml-2 h-3 w-3 shrink-0 text-sidebar-muted" />
        </button>

        <p className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">
          Home
        </p>
        <ul className="space-y-0.5">{visibleHomeNav.map(renderNavItem)}</ul>
        <p className="mb-1.5 mt-5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">
          CRM
        </p>
        <ul className="space-y-0.5">{crmNav.map(renderNavItem)}</ul>
        <p className="mb-1.5 mt-5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">
          Engage
        </p>
        <ul className="space-y-0.5">{engageNav.map(renderNavItem)}</ul>
        <p className="mb-1.5 mt-5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">
          Accounts
        </p>
        <ul className="space-y-0.5">{accountsNav.map(renderNavItem)}</ul>
        <p className="mb-1.5 mt-5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">
          Analytics
        </p>
        <ul className="space-y-0.5">{analyticsNav.map(renderNavItem)}</ul>
        <p className="mb-1.5 mt-5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-muted">
          Workspace
        </p>
        <ul className="space-y-0.5">{workspaceNav.map(renderNavItem)}</ul>
      </div>
      <ul className="space-y-0.5 pb-1">{bottomNav.map(renderNavItem)}</ul>
    </div>
  );

  return (
    <>
      {mobileOpen ? (
        <button
          type="button"
          className="fixed inset-0 z-[60] bg-dark-black/40 lg:hidden"
          aria-label="Close menu overlay"
          onClick={onClose}
        />
      ) : null}

      <aside
        className={[
          "font-inter z-[70] h-[100dvh] w-[252px] shrink-0 overflow-y-auto bg-dark-blue px-3.5 py-[22px] text-sidebar-fg",
          "fixed inset-y-0 left-0 transform transition-transform duration-200 lg:static lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        ].join(" ")}
      >
        {navBody}
      </aside>
      {showWorkSpaceOptions ? (
        <WorkSpaceSelectionModal onClose={() => setShowWorkSpaceOptions(false)} />
      ) : null}
    </>
  );
};

export default Sidebar;
